import { useEffect, useMemo, useRef, useState } from "react";
import { Instagram } from "lucide-react";
import playsGraphic from "@/assets/1000000-plays-graphic.jpg";

function useInView<T extends HTMLElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setInView(true);
      });
    }, { threshold: 0.2, ...(options || {}) });
    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);
  return { ref, inView } as const;
}

function CountUpNumber({ end, duration = 1500 }: { end: number; duration?: number }) {
  const [val, setVal] = useState(0);
  const startTime = useRef<number | null>(null);
  useEffect(() => {
    let raf = 0;
    const step = (t: number) => {
      if (startTime.current == null) startTime.current = t;
      const elapsed = t - startTime.current;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.floor(end * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);

  const formatted = useMemo(() => val.toLocaleString(), [val]);
  return <>{formatted}</>;
}

function BrandIconX() {
  return (
    <div className="w-12 h-12 rounded-lg bg-black text-white flex items-center justify-center shadow-md">
      <span className="text-xl font-black tracking-tight">X</span>
    </div>
  );
}

export default function SocialStats() {
  const { ref, inView } = useInView<HTMLDivElement>();
  const stats = {
    twitter: [
      { end: 36000, label: "followers" },
      { end: 17000000, label: "impressions" },
      { end: 1200000, label: "media/video views" },
      { end: 30000, label: "reposts" },
    ],
    instagram: [
      { end: 25000, label: "followers" },
      { end: 20000000, label: "views" },
      { end: 1400000, label: "accounts reached" },
      { end: 350000, label: "interactions on posts" },
    ],
  } as const;

  return (
    <section ref={ref} className="relative pt-24 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/lovable-uploads/e328267e-d668-43c6-b694-b40e8caeebf8.png" alt="Basketball texture" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h3 className="text-3xl md:text-4xl font-black tracking-tight text-white mb-8 text-center">
          RECRUITLOOK HOOPS <span className="text-secondary">SOCIAL MEDIA STATS</span> IN 2025
        </h3>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left column: X and Instagram stacked */}
          <div className="grid gap-6">
            <div className="rounded-2xl p-6 border border-white/10 bg-slate-900/85 text-white h-full">
              <div className="flex items-center gap-3 mb-4">
                <BrandIconX />
                <h4 className="text-xl font-black text-secondary">X</h4>
              </div>
              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                <ul className="space-y-3 order-2 md:order-1">
                   {stats.twitter.map((s, i) => (
                     <li key={i} className="text-white">
                       <span className="text-2xl font-black text-white">
                         {s.end.toLocaleString()}+{" "}
                       </span>
                       <span className="font-semibold text-white/90">{s.label}</span>
                     </li>
                   ))}
                </ul>
                <div className="order-1 md:order-2 h-32 md:h-full">
                  <img src="/lovable-uploads/c5790929-f575-4134-aa6f-cfd355d5a91f.png" alt="X logo" className="w-full h-full object-cover rounded-md" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-6 border border-white/10 bg-slate-900/85 text-white h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500 via-purple-500 to-yellow-400 text-white flex items-center justify-center shadow-md">
                  <Instagram className="w-6 h-6" />
                </div>
                <h4 className="text-xl font-black text-secondary">INSTAGRAM</h4>
              </div>
              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                <ul className="space-y-3 order-2 md:order-1">
                   {stats.instagram.map((s, i) => (
                     <li key={i} className="text-white">
                       <span className="text-2xl font-black text-white">
                         {s.end.toLocaleString()}+{" "}
                       </span>
                       <span className="font-semibold text-white/90">{s.label}</span>
                     </li>
                   ))}
                </ul>
                <div className="order-1 md:order-2 h-32 md:h-full">
                  <img src="https://cdn.builder.io/api/v1/image/assets%2F7b5b7573f41a45539311717edd1f85db%2F2041e4f4db97495b9171af5e1870a6c3?format=webp&width=800" alt="Instagram logo" className="w-full h-full object-cover rounded-md" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: emphasis card + caption */}
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-slate-900 text-white rounded-2xl shadow p-8 border border-white/10 text-center">
              <div className="text-4xl md:text-5xl font-black tracking-tight">
                1,000,000 <span className="text-secondary">PLAYS</span>
              </div>
              <p className="mt-2 text-white/80">Go viral on RecruitLook!</p>
            </div>
            
            {/* 1,000,000 Plays Graphic */}
            <div className="mt-8 flex justify-center">
              <img 
                src={playsGraphic}
                alt="1,000,000 Plays RecruitLook Hoops graphic with mobile phone interface"
                className="max-w-full h-auto max-h-96 rounded-lg shadow-lg"
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
